// ─────────────────────────────────────────────────────────────────────────────
// ENTITIES  –  moving platforms, enemies, particles
// ─────────────────────────────────────────────────────────────────────────────

import { Platform, Enemy, GROUND_Y, NEON } from '../data/levelData';

// ─── Moving platform offsets ─────────────────────────────────────────────────
/**
 * Returns a Map<platformIndex, {ox, oy}> for every moving platform.
 * `time` is elapsed game seconds.
 */
export function calcMovingOffsets(
  platforms: Platform[],
  time: number,
): Map<number, { ox: number; oy: number }> {
  const map = new Map<number, { ox: number; oy: number }>();
  for (let i = 0; i < platforms.length; i++) {
    const p = platforms[i];
    if (!p.moving) continue;
    const range = p.moveRange ?? 80;
    const speed = p.moveSpeed ?? 60;
    const phase = Math.sin((time * speed) / range);
    if (p.moveAxis === 'x') {
      map.set(i, { ox: phase * range, oy: 0 });
    } else {
      map.set(i, { ox: 0, oy: phase * range });
    }
  }
  return map;
}

// ─── Enemy runtime state ─────────────────────────────────────────────────────
export type EnemyState = Enemy & {
  dir: 1 | -1;
  vDir: 1 | -1;
  dead: boolean;
  type: 'ground' | 'flying';
};

export function initEnemies(enemies: Enemy[]): EnemyState[] {
  return enemies.map(e => ({
    ...e,
    dir: 1 as const,
    vDir: 1 as const,
    dead: false,
    type: e.type ?? 'ground',
  }));
}

export function stepEnemies(enemies: EnemyState[], dt: number, time: number): void {
  for (const e of enemies) {
    if (e.dead) continue;

    if (e.type === 'flying') {
      e.x += e.dir * e.speed * dt;
      if (e.x > e.patrolRight) { e.x = e.patrolRight; e.dir = -1; }
      if (e.x < e.patrolLeft)  { e.x = e.patrolLeft;  e.dir =  1; }

      const top = e.patrolTop ?? e.y - 40;
      const bot = e.patrolBottom ?? e.y + 40;
      // bob + patrol
      e.y += e.vDir * e.speed * 0.55 * dt;
      if (e.y > bot) { e.y = bot; e.vDir = -1; }
      if (e.y < top) { e.y = top; e.vDir =  1; }
      // subtle sine wobble
      e.y += Math.sin(time * 3 + e.id) * 8 * dt;
    } else {
      e.x += e.dir * e.speed * dt;
      if (e.x > e.patrolRight) { e.x = e.patrolRight; e.dir = -1; }
      if (e.x < e.patrolLeft)  { e.x = e.patrolLeft;  e.dir =  1; }
    }
  }
}

/** Check if player AABB overlaps an enemy AABB. */
export function playerHitsEnemy(
  px: number, py: number, pw: number, ph: number,
  e: EnemyState,
): boolean {
  if (e.dead) return false;
  return px < e.x + e.w && px + pw > e.x &&
         py < e.y + e.h && py + ph > e.y;
}

// ─── Particles ───────────────────────────────────────────────────────────────
export type Particle = {
  x:    number;
  y:    number;
  vx:   number;
  vy:   number;
  life: number;   // remaining seconds
  max:  number;   // initial life
  r:    number;   // radius
  col:  string;
};

export function spawnDeathParticles(
  cx: number,
  cy: number,
  out: Particle[],
): void {
  for (let i = 0; i < 28; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 120 + Math.random() * 280;
    out.push({
      x: cx, y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 80,
      life: 0.6 + Math.random() * 0.5,
      max:  0.6 + Math.random() * 0.5,
      r:    2 + Math.random() * 4,
      col:  NEON,
    });
  }
}

export function spawnJetpackFlame(
  x: number,
  y: number,
  out: Particle[],
): void {
  if (out.length > 160) return;
  out.push({
    x: x + (Math.random() - 0.5) * 10,
    y: y,
    vx: (Math.random() - 0.5) * 40,
    vy: 80 + Math.random() * 120,
    life: 0.2 + Math.random() * 0.2,
    max:  0.2 + Math.random() * 0.2,
    r:    2 + Math.random() * 3,
    col:  Math.random() < 0.5 ? '#ffe066' : '#ff8844',
  });
}

export function spawnAmbientParticle(
  worldLeft: number,
  viewW:     number,
  out:       Particle[],
): void {
  if (out.length > 120) return;
  out.push({
    x:    worldLeft + Math.random() * viewW,
    y:    GROUND_Y - Math.random() * 700,
    vx:   (Math.random() - 0.5) * 20,
    vy:   -15 - Math.random() * 30,
    life: 3 + Math.random() * 4,
    max:  3 + Math.random() * 4,
    r:    1 + Math.random() * 2,
    col:  Math.random() < 0.5 ? NEON : '#b44fff',
  });
}

export function stepParticles(particles: Particle[], dt: number): void {
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i];
    p.x    += p.vx * dt;
    p.y    += p.vy * dt;
    p.vy   += 60 * dt;   // slight drag
    p.life -= dt;
    if (p.life <= 0) particles.splice(i, 1);
  }
}
